import { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ProgressSpinner } from 'primereact/progressspinner';
import axios from 'axios';
import { BarrioContext } from '../components/BarrioContext';
import { useAuth } from '../components/AuthContext';
import { useTerminologia } from '../hooks/useTerminologia';

const formatMoney = (n) =>
    new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(n ?? 0);

const formatPeriodo = (p) => {
    if (!p) return '';
    const d = new Date(p);
    const label = d.toLocaleDateString('es-AR', { month: 'long', year: 'numeric' });
    return label.charAt(0).toUpperCase() + label.slice(1);
};

const estadoBadge = (row) => {
    const alDia = (row.saldoFinal ?? 0) <= 0;
    const s = alDia
        ? { bg: '#ecfdf3', color: '#12b76a', border: '#a9efc5', label: 'Al día' }
        : { bg: '#fef3f2', color: '#f04438', border: '#fecdca', label: 'Pendiente' };
    return (
        <span style={{
            background: s.bg, color: s.color, border: `1px solid ${s.border}`,
            fontWeight: 600, fontSize: '0.75rem', padding: '0.15rem 0.6rem', borderRadius: 99,
        }}>
            {s.label}
        </span>
    );
};

const EstadoCuenta = () => {
    const HOST_API = process.env.REACT_APP_URL;
    const { barrio } = useContext(BarrioContext);
    const { administracionId } = useAuth();
    const t = useTerminologia();

    const usuarioId = (() => { try { const u = JSON.parse(localStorage.getItem('user') || '{}'); return u.id || u.usuarioId || 0; } catch { return 0; } })();

    const [ufs, setUfs] = useState([]);
    const [ufSel, setUfSel] = useState(null);
    const [historial, setHistorial] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!barrio?.id || !usuarioId) return;
        axios.get(`${HOST_API}api/unidadesFuncionales/GetUFsByUsuario?usuarioId=${usuarioId}&barrioId=${barrio.id}`)
            .then(({ data }) => {
                setUfs(data ?? []);
                setUfSel((prev) => prev ?? (data?.[0]?.unidadFuncionalId ?? null));
            })
            .catch(() => {});
    }, [barrio?.id, usuarioId, HOST_API]);

    const fetchHistorial = useCallback(async () => {
        if (!barrio?.id || !ufSel) return;
        setLoading(true);
        try {
            const { data } = await axios.get(`${HOST_API}api/estadoCuentas/GetEstadoCuenta`, {
                params: { barrioId: barrio.id, unidadFuncionalId: ufSel, administracionId },
            });
            setHistorial(data ?? []);
        } catch {
            setHistorial([]);
        } finally {
            setLoading(false);
        }
    }, [barrio?.id, ufSel, administracionId, HOST_API]);

    useEffect(() => { fetchHistorial(); }, [fetchHistorial]);

    const ufLabel = useMemo(() => ufs.find(u => u.unidadFuncionalId === ufSel)?.unidadFuncional ?? '', [ufs, ufSel]);

    return (
        <>
            <h4>Estado de Cuenta</h4>
            {ufLabel && <p style={{ color: '#667085', fontSize: '0.9rem', marginTop: '-0.5rem' }}>{t.UnidadFuncional}: {ufLabel}</p>}
            <br />

            {ufs.length > 1 && (
                <div className="flex align-items-center gap-3 mb-4">
                    <Dropdown
                        value={ufSel}
                        options={ufs}
                        optionLabel="unidadFuncional"
                        optionValue="unidadFuncionalId"
                        onChange={(e) => setUfSel(e.value)}
                        className="p-inputtext-sm"
                        style={{ minWidth: 220 }}
                    />
                </div>
            )}

            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
                    <ProgressSpinner style={{ width: 48, height: 48 }} strokeWidth="4" />
                </div>
            ) : (
                <div className="grid">
                    <div className="col-12">
                        <DataTable
                            value={historial}
                            paginator
                            rows={12}
                            rowsPerPageOptions={[12, 24, 50]}
                            responsiveLayout="scroll"
                            emptyMessage="No hay liquidaciones registradas para esta unidad"
                            sortField="periodo"
                            sortOrder={-1}
                            style={{ fontSize: '0.88rem' }}
                        >
                            <Column field="periodo" header="Período" sortable body={(r) => formatPeriodo(r.periodo)} style={{ minWidth: 150 }} />
                            <Column field="expensaMes" header="Expensa del mes" sortable body={(r) => formatMoney(r.expensaMes)} style={{ minWidth: 140 }} />
                            <Column field="intereses" header="Intereses" sortable body={(r) => formatMoney(r.intereses)} style={{ minWidth: 120 }} />
                            <Column field="planPago" header="Plan de pago" body={(r) => r.tienePlanPago ? formatMoney(r.planPago) : '—'} style={{ minWidth: 130 }} />
                            <Column field="pagosRealizados" header="Pagos realizados" sortable body={(r) => formatMoney(r.pagosRealizados)} style={{ minWidth: 150 }} />
                            <Column
                                field="saldoFinal"
                                header="Saldo"
                                sortable
                                body={(r) => (
                                    <span style={{ fontWeight: 700, color: (r.saldoFinal ?? 0) <= 0 ? '#12b76a' : '#f04438' }}>
                                        {formatMoney(r.saldoFinal)}
                                    </span>
                                )}
                                style={{ minWidth: 130 }}
                            />
                            <Column field="fechaVencimiento" header="Vencimiento" body={(r) => r.fechaVencimiento ? new Date(r.fechaVencimiento).toLocaleDateString('es-AR') : '—'} style={{ minWidth: 120 }} />
                            <Column header="Estado" body={estadoBadge} style={{ minWidth: 110 }} />
                        </DataTable>
                    </div>
                </div>
            )}
        </>
    );
};

export default EstadoCuenta;
